/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import css from '@emotion/css/macro'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import authActions from '@sportninja/common/actions/auth'
import { EMAIL_LENGTH_MAX, ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'

import Button from 'src/components/ts-components/Button'
import Helmet from 'src/components/Helmet'
import CanlanTextLogo from 'src/components/icons/CanlanTextLogo'
import SportNinjaLogo from 'src/components/ts-components/SportNinjaLogo'
import Text from 'src/components/ts-components/Text'
import { TextInput } from 'src/components/ts-components/TextInput'

type ResendError = {
  status?: number
  message?: string
  invalid_fields?: {
    validated_at?: string
  }
}

type RegisterSuccessProps = {
  resend: (email: string) => Promise<unknown>
}

/**
 * Confirmation after creating an account, in the same centered column as the
 * invitation signup screen. The address comes from route state and is lost
 * on refresh, which shows the fallback.
 */
const RegisterSuccess = ({ resend }: RegisterSuccessProps) => {
  'use memo'

  const history = useHistory()
  const location = useLocation<{ email?: string }>()
  const initialEmail = location.state?.email || ''
  const [email, setEmail] = useState(initialEmail)
  const [fieldError, setFieldError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)

  const onResend = async () => {
    if (!email.trim()) {
      setFieldError(t('errors:enterAnEmail', {}))
      return
    }

    setIsLoading(true)
    setError('')
    setFieldError('')
    setSent(false)

    try {
      await resend(email.trim())
      setSent(true)
    } catch (resendError) {
      setError(getResendErrorMessage(resendError))
    } finally {
      setIsLoading(false)
    }
  }

  const logo = isCanlan ? (
    <CanlanTextLogo height={32} color={colors.PRIMARY} />
  ) : (
    <div css={styles.sportninjaLogo}>
      <SportNinjaLogo />
    </div>
  )

  return (
    <div css={styles.page}>
      <Helmet
        title={t('util:capitalize', { text: t('common:register', {}) })}
      />
      <div css={styles.column}>
        <div css={styles.header}>
          <div css={styles.logo}>{logo}</div>
          <Text
            variant='heading2'
            weight='semiBold'
            color={colors.NEUTRAL_0}
            style={'text-align: center;'}
          >
            {initialEmail
              ? `${capitalizeWords(t('Auth:almostReady', {}))}!`
              : 'Oops...'}
          </Text>
        </div>

        {initialEmail ? (
          <>
            <div css={styles.copy}>
              <Text
                variant='paragraphMedium'
                weight='regular'
                color={colors.NEUTRAL_0}
                style={'text-align: center;'}
              >
                {t('Web:inviteRegisterVerify', {})}
              </Text>
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
                style={'text-align: center;'}
              >
                {t('Web:pleaseClickButtonInEmail', {})}
              </Text>
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
                style={'text-align: center;'}
              >
                {t('Web:ifYouCantFindItHint', {})}
              </Text>
            </div>
            <form
              css={styles.form}
              noValidate
              onSubmit={(event) => {
                event.preventDefault()
                onResend()
              }}
            >
              {error ? (
                <div css={styles.banner} role='alert'>
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.ERROR_LIGHT}
                  >
                    {error}
                  </Text>
                </div>
              ) : null}
              <div css={styles.field}>
                <TextInput
                  name='email'
                  value={email}
                  setValue={setEmail}
                  fieldErrors={fieldError ? { email: fieldError } : {}}
                  customError={null}
                  label={capitalizeWords(t('common:email', {}))}
                  placeholder={capitalizeWords(t('common:email', {}))}
                  type='email'
                  maxLength={EMAIL_LENGTH_MAX}
                  disabled={isLoading}
                  error={Boolean(fieldError)}
                  required
                />
              </div>
              <Button
                text={t('Auth:resendEmail', { defaultValue: 'Resend Email' })}
                variant='primary'
                onClick={onResend}
                isLoading={isLoading}
                disabled={isLoading}
                style={styles.submit}
              />
              {sent ? (
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                  style={'text-align: center;'}
                >
                  {t('Auth:confirmEmailSent', {
                    defaultValue:
                      'We have sent you a confirmation email. Open it and click the link to confirm your account.',
                  })}
                </Text>
              ) : null}
            </form>
          </>
        ) : (
          <div css={styles.copy}>
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.NEUTRAL_50}
              style={'text-align: center;'}
            >
              {t('Web:sentToWrongPlace', {
                defaultValue:
                  'Looks like you were sent to the wrong place. Click the button below to go back to the homepage.',
              })}
            </Text>
            <Button
              text={t('Web:goHome', { defaultValue: 'Go Home' })}
              variant='primary'
              onClick={() => history.push(ROUTES.HOME)}
              style={styles.submit}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const getResendErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') {
    return t('errors:somethingWentWrongPleaseTryAgain', {})
  }

  const apiError = error as ResendError
  if (apiError.status === 400 && apiError.invalid_fields?.validated_at) {
    return t('errors:emailAlreadyVerified', {
      defaultValue:
        'This email address has already been used to verify an account.',
    })
  }
  if (apiError.status === 404) {
    return t('errors:couldNotFindAccount', {
      defaultValue:
        'We could not find an account associated with that email address.',
    })
  }

  return apiError.message || t('errors:somethingWentWrongPleaseTryAgain', {})
}

const mapDispatchToProps = (dispatch) => ({
  resend: bindActionToPromise(dispatch, authActions.resend.request),
})

export default connect(null, mapDispatchToProps)(RegisterSuccess)

const styles = {
  page: css`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 16px;
    padding-block: 48px;
    background: ${colors.SECONDARY_400};
  `,
  column: css`
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
    max-width: 650px;
  `,
  header: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
  `,
  logo: css`
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;
  `,
  sportninjaLogo: css`
    display: flex;

    svg {
      height: 48px;
      width: auto;
      max-width: 100%;
    }
  `,
  copy: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
  `,
  form: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  `,
  field: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  `,
  submit: css`
    width: 100%;
    flex: none;
  `,
  banner: css`
    border-radius: 8px;
    border: 1px solid ${colors.ERROR_300};
    background: rgba(217, 27, 27, 0.12);
    padding: 8px 12px;
  `,
}
